import {
  CanvasTexture,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  type PerspectiveCamera,
  RingGeometry,
  Sprite,
  SpriteMaterial,
  TextureLoader,
  Vector3,
} from "three";
import { latLonToLocal } from "./geo";
import type { HuntStop } from "./stops";

export type HuntMarker = {
  stop: HuntStop;
  group: Group;
  core: Mesh;
  ring: Mesh;
  beam: Mesh;
  label: Sprite;
  photo: Sprite | null;
  hasHeroProp: boolean;
};

const HERO_PROP_IDS = new Set([
  "chateau-frontenac",
  "porte-saint-louis",
  "terrasse-dufferin",
  "san-juan-bosco",
]);

function makeLabelTexture(text: string): CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "rgba(18, 22, 27, 0.82)";
  ctx.fillRect(16, 24, 480, 80);
  ctx.strokeStyle = "rgba(196, 163, 90, 0.7)";
  ctx.lineWidth = 3;
  ctx.strokeRect(16, 24, 480, 80);
  ctx.fillStyle = "#f3efe6";
  ctx.font = "600 36px Outfit, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, 256, 64, 440);
  const tex = new CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

export function createHuntMarkers(stops: HuntStop[]): HuntMarker[] {
  const loader = new TextureLoader();
  return stops.map((stop) => {
    const hasHeroProp = HERO_PROP_IDS.has(stop.id);
    const group = new Group();
    // Markers for hero props sit lower — the mesh is the landmark
    const markerHeight = hasHeroProp ? 4 : stop.height;
    const pos = latLonToLocal(stop.lat, stop.lon, markerHeight);
    group.position.copy(pos);

    const core = new Mesh(
      new CylinderGeometry(hasHeroProp ? 1.6 : 2.2, hasHeroProp ? 1.6 : 2.2, 4.5, 20),
      new MeshBasicMaterial({ color: 0xc4a35a }),
    );
    core.position.y = 2.2;

    const beam = new Mesh(
      new CylinderGeometry(0.35, 0.35, hasHeroProp ? 55 : 24, 10),
      new MeshBasicMaterial({
        color: 0xe8c872,
        transparent: true,
        opacity: 0.35,
      }),
    );
    beam.position.y = hasHeroProp ? 32 : 16;

    const ring = new Mesh(
      new RingGeometry(hasHeroProp ? 12 : 5.5, hasHeroProp ? 16 : 9.5, 40),
      new MeshBasicMaterial({
        color: 0xf3efe6,
        transparent: true,
        opacity: 0.55,
        side: DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -markerHeight + 0.25;

    const label = new Sprite(
      new SpriteMaterial({
        map: makeLabelTexture(stop.name),
        transparent: true,
        depthTest: false,
      }),
    );
    label.scale.set(40, 10, 1);
    label.position.y =
      stop.id === "chateau-frontenac" ? 88 : hasHeroProp ? 68 : 32;

    let photo: Sprite | null = null;
    if (!hasHeroProp) {
      const photoMat = new SpriteMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.98,
        depthTest: false,
      });
      photo = new Sprite(photoMat);
      photo.scale.set(36, 24, 1);
      photo.position.set(0, 14, 0);
      photo.visible = false;
      loader.load(stop.photo, (tex) => {
        photoMat.map = tex;
        photoMat.needsUpdate = true;
      });
      group.add(photo);
    }

    group.add(core, beam, ring, label);
    return { stop, group, core, ring, beam, label, photo, hasHeroProp };
  });
}

export function updateMarkerStyles(
  markers: HuntMarker[],
  selectedId: string | null,
  found: Set<string>,
  t: number,
  _camera?: PerspectiveCamera,
) {
  for (const m of markers) {
    const markerHeight = m.hasHeroProp ? 4 : m.stop.height;
    const base = latLonToLocal(m.stop.lat, m.stop.lon, markerHeight);
    const active = m.stop.id === selectedId;
    const isFound = found.has(m.stop.id);
    const pulse = active ? 1 + Math.sin(t * 3) * 0.12 : 1;

    m.group.position.set(
      base.x,
      base.y + (active && !m.hasHeroProp ? Math.sin(t * 2.2) * 1.2 : 0),
      base.z,
    );
    m.core.scale.setScalar(pulse);
    m.beam.visible = active || !isFound;
    m.beam.scale.y = active ? 1.25 + Math.sin(t * 2) * 0.15 : 0.7;
    (m.beam.material as MeshBasicMaterial).opacity = active ? 0.55 : 0.18;
    m.label.visible = active;
    if (m.photo) m.photo.visible = active;

    const mat = m.core.material as MeshBasicMaterial;
    if (isFound) mat.color.set(0x7cb89a);
    else if (active) mat.color.set(0xe8c872);
    else mat.color.set(0xc4a35a);

    (m.ring.material as MeshBasicMaterial).opacity = active ? 0.85 : 0.3;
  }
}

export function flyOffsetForStop(stopId?: string): Vector3 {
  if (stopId === "chateau-frontenac") {
    return new Vector3(70, 48, 95);
  }
  if (stopId === "porte-saint-louis") {
    return new Vector3(45, 32, 55);
  }
  if (stopId === "terrasse-dufferin") {
    return new Vector3(50, 28, 70);
  }
  // La Fortuna postcard beats
  if (stopId === "san-juan-bosco") {
    return new Vector3(-35, 22, 48);
  }
  if (stopId === "parque-central") {
    return new Vector3(-28, 18, 42);
  }
  if (stopId === "arenal-view") {
    return new Vector3(-55, 28, 30);
  }
  if (stopId === "calle-sur") {
    return new Vector3(20, 16, 55);
  }
  return new Vector3(55, 42, 75);
}
